import { useMemo, useState } from 'react';
import { contrastInk } from '../domain/color';
import { LIBRARIES, customColor, parsePaletteCsv, paletteToCsv } from '../domain/palettes';
import {
  clonePattern,
  colorCounts,
  compactPalette,
  ensureColor,
  removeColorAt,
  swapColor,
  symbolForIndex,
} from '../domain/pattern';
import { EMPTY } from '../domain/types';
import type { BeadColor } from '../domain/types';
import type { ToolId } from '../editor/store';
import { downloadText, safeFilename } from '../io/format';
import { Slider, Toggle, pickFile, useEditor } from './kit';

const TOOLS: { id: ToolId; glyph: string; label: string; key: string }[] = [
  { id: 'pencil', glyph: '✎', label: 'Pencil', key: 'B' },
  { id: 'eraser', glyph: '⌫', label: 'Eraser', key: 'E' },
  { id: 'line', glyph: '╱', label: 'Line', key: 'L' },
  { id: 'rect', glyph: '▭', label: 'Rectangle', key: 'R' },
  { id: 'ellipse', glyph: '◯', label: 'Ellipse', key: 'O' },
  { id: 'fill', glyph: '◧', label: 'Flood fill', key: 'G' },
  { id: 'replace', glyph: '⇄', label: 'Replace colour everywhere', key: 'H' },
  { id: 'picker', glyph: '⊙', label: 'Pick colour', key: 'I' },
  { id: 'select', glyph: '⬚', label: 'Select / move', key: 'M' },
  { id: 'pan', glyph: '✥', label: 'Pan (or hold Alt)', key: 'Space' },
];

export function LeftPanel() {
  const { store, state } = useEditor();
  const pattern = state.pattern;
  const [libraryId, setLibraryId] = useState('delica11');
  const [search, setSearch] = useState('');
  const [swapFrom, setSwapFrom] = useState<number | null>(null);
  const [customHex, setCustomHex] = useState('#c94f4f');

  const library = LIBRARIES.find((l) => l.id === libraryId) ?? LIBRARIES[0];
  const counts = useMemo(() => colorCounts(pattern), [pattern, state.rev]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return library.colors;
    return library.colors.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.hex.includes(q),
    );
  }, [library, search]);

  const addFromLibrary = (color: BeadColor) => {
    if (swapFrom !== null) {
      store.commit((p) => {
        const next = clonePattern(p);
        const to = ensureColor(next, color);
        return swapColor(next, swapFrom, to);
      }, `Swapped to ${color.name}`);
      setSwapFrom(null);
      return;
    }
    store.addColor(color);
  };

  const importPaletteCsv = async () => {
    const file = await pickFile('.csv,text/csv,text/plain');
    if (!file) return;
    const text = await file.text();
    const colors = parsePaletteCsv(text, file.name.replace(/\.[^.]+$/, ''));
    if (colors.length === 0) {
      store.status('No colours found — expected lines like: DB0010,Opaque Black,#141414');
      return;
    }
    store.commit((p) => {
      const next = clonePattern(p);
      for (const c of colors) ensureColor(next, c);
      return next;
    }, `Imported ${colors.length} colours`);
  };

  return (
    <aside className="left-panel">
      <section className="tools">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            className={`tool-btn${state.tool === t.id ? ' active' : ''}`}
            title={`${t.label} (${t.key})`}
            onClick={() => store.set({ tool: t.id })}
          >
            <span className="tool-glyph">{t.glyph}</span>
          </button>
        ))}
      </section>

      <section className="panel-block">
        <div className="mini-label">Brush</div>
        <Slider value={state.brushSize} onChange={(v) => store.set({ brushSize: v })} min={1} max={9} />
        {(state.tool === 'rect' || state.tool === 'ellipse') && (
          <Toggle checked={state.shapeFilled} onChange={(v) => store.set({ shapeFilled: v })} label="Filled shape" />
        )}
        <div className="mini-label" style={{ marginTop: 8 }}>
          Mirror
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button
            className={`chip${state.symH ? ' active' : ''}`}
            onClick={() => store.set({ symH: !state.symH })}
            title="Mirror left/right while drawing"
          >
            ↔ Left/right
          </button>
          <button
            className={`chip${state.symV ? ' active' : ''}`}
            onClick={() => store.set({ symV: !state.symV })}
            title="Mirror top/bottom while drawing"
          >
            ↕ Top/bottom
          </button>
        </div>
      </section>

      <section className="panel-block palette-block">
        <div className="mini-label row-between">
          <span>Pattern colours</span>
          <span className="dim">{pattern.palette.length - 1}</span>
        </div>
        <div className="swatch-grid">
          <button
            className={`swatch empty${state.primary === EMPTY ? ' selected' : ''}`}
            title="Empty — no bead"
            onClick={() => store.set({ primary: EMPTY })}
          >
            ∅
          </button>
          {pattern.palette.map((c, i) => {
            if (i === EMPTY) return null;
            return (
              <button
                key={`${c.id}-${i}`}
                className={`swatch${state.primary === i ? ' selected' : ''}${swapFrom === i ? ' swapping' : ''}`}
                style={{ background: c.hex, color: contrastInk(c.hex) }}
                title={`${c.code ? `${c.code} — ` : ''}${c.name}\n${counts.get(i) ?? 0} beads\nShift-click to set as right-click colour`}
                onClick={(e) => {
                  if (e.shiftKey) store.set({ secondary: i });
                  else store.set({ primary: i });
                }}
              >
                {state.view.showSymbols ? symbolForIndex(i) : ''}
              </button>
            );
          })}
        </div>

        {state.primary !== EMPTY && pattern.palette[state.primary] && (
          <div className="selected-color">
            <div className="selected-name">
              {pattern.palette[state.primary].code && (
                <strong>{pattern.palette[state.primary].code} </strong>
              )}
              {pattern.palette[state.primary].name}
            </div>
            <div className="dim small">
              {counts.get(state.primary) ?? 0} beads
              {pattern.palette[state.primary].finish ? ` · ${pattern.palette[state.primary].finish}` : ''}
            </div>
            <div className="row" style={{ gap: 6, marginTop: 6 }}>
              <button
                className={`chip${swapFrom === state.primary ? ' active' : ''}`}
                onClick={() => setSwapFrom(swapFrom === state.primary ? null : state.primary)}
                title="Then pick a colour below to repaint every bead of this colour"
              >
                ⇄ Swap
              </button>
              <button
                className="chip"
                onClick={() => {
                  const idx = state.primary;
                  store.commit((p) => removeColorAt(p, idx), 'Removed colour');
                  store.set({ primary: EMPTY });
                }}
                title="Remove from the palette; its beads become empty"
              >
                Remove
              </button>
            </div>
          </div>
        )}

        <div className="row" style={{ gap: 6, marginTop: 8 }}>
          <button
            className="chip"
            onClick={() => store.commit((p) => compactPalette(p), 'Removed unused colours')}
            title="Drop palette colours no bead uses"
          >
            Tidy
          </button>
          <button className="chip" onClick={importPaletteCsv} title="Load a colour list from CSV">
            Import CSV
          </button>
          <button
            className="chip"
            onClick={() =>
              downloadText(
                `${safeFilename(pattern.name)}-palette.csv`,
                paletteToCsv(pattern.palette),
                'text/csv',
              )
            }
          >
            Export CSV
          </button>
        </div>
      </section>

      <section className="panel-block library-block">
        <div className="mini-label">Bead library</div>
        <select value={libraryId} onChange={(e) => setLibraryId(e.target.value)}>
          {LIBRARIES.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </select>
        <input
          className="search"
          placeholder="Search name or code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {swapFrom !== null && (
          <div className="banner">Pick a colour to replace every bead of the selected colour.</div>
        )}
        <div className="swatch-grid library-grid">
          {filtered.map((c) => (
            <button
              key={c.id}
              className="swatch"
              style={{ background: c.hex }}
              title={`${c.code ? `${c.code} — ` : ''}${c.name}${c.finish ? ` (${c.finish})` : ''}`}
              onClick={() => addFromLibrary(c)}
            />
          ))}
          {filtered.length === 0 && <div className="dim small">No colours match “{search}”.</div>}
        </div>
        {library.note && <p className="dim tiny">{library.note}</p>}

        <div className="mini-label" style={{ marginTop: 10 }}>
          Custom colour
        </div>
        <div className="row" style={{ gap: 6 }}>
          <input type="color" value={customHex} onChange={(e) => setCustomHex(e.target.value)} />
          <button className="chip" onClick={() => addFromLibrary(customColor(customHex))}>
            Add
          </button>
        </div>
      </section>
    </aside>
  );
}
